/**
 * Bits as the API writes them: a plane at a position is a bigint mask, sent as
 * lowercase hex ("0x600") or as slot indices ([9, 10]). Everything that draws,
 * folds or predicts works on the bigint; hex and slot lists are only for the
 * wire and for people. Pure.
 */

import { runs } from '../../../console/src/grid.ts';

/** A mask as the API writes it: "0x600", "0x0". */
export const hex = (mask: bigint): string => `0x${mask.toString(16)}`;

/** "0x600" (or "600") → 0x600n; null when it does not read as hex. */
export const fromHex = (text: string): bigint | null => {
  const body = text.trim().toLowerCase().replace(/^0x/, '');
  if (!/^[0-9a-f]+$/.test(body)) return null;
  return BigInt(`0x${body}`);
};

/** A mask the server sent; a malformed one reads as empty rather than throwing mid-render. */
export const maskOf = (value: unknown): bigint => {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string') return fromHex(value) ?? 0n;
  if (Array.isArray(value)) return slotsMask(value.filter((s): s is number => Number.isInteger(s) && s >= 0));
  return 0n;
};

/** Slot indices → mask: [9, 10] → 0x600n. */
export const slotsMask = (slots: readonly number[]): bigint =>
  slots.reduce((acc, slot) => acc | (1n << BigInt(slot)), 0n);

/** Mask → slot indices, lowest first: 0x600n → [9, 10]. */
export const slotsOf = (mask: bigint): number[] => {
  const out: number[] = [];
  for (let slot = 0, m = mask; m > 0n; slot += 1, m >>= 1n) if (m & 1n) out.push(slot);
  return out;
};

/** Every slot of a position lit: `bits` ones. */
export const full = (bits: number): bigint => (bits <= 0 ? 0n : (1n << BigInt(bits)) - 1n);

/** The slots of a position that are not in `mask`. */
export const invert = (mask: bigint, bits: number): bigint => full(bits) & ~mask;

export const popcount = (mask: bigint): number => {
  let n = 0;
  for (let m = mask; m > 0n; m &= m - 1n) n += 1;
  return n;
};

/** Shift toward slot 0; bits that fall off the bottom are gone. */
export const shiftDown = (mask: bigint, n: number): bigint => (n <= 0 ? mask : mask >> BigInt(n));

/** Shift toward the top, kept inside `bits`. */
export const shiftUp = (mask: bigint, n: number, bits: number): bigint => (n <= 0 ? mask : (mask << BigInt(n)) & full(bits));

export interface Run { readonly from: number; readonly to: number; readonly length: number; readonly mask: bigint }

/** Slots a..b (either order), inclusive. */
export const range = (a: number, b: number): bigint => {
  const lo = Math.max(0, Math.min(a, b));
  const hi = Math.max(a, b);
  return full(hi - lo + 1) << BigInt(lo);
};

/** The unbroken stretches of lit slots: 0x3c600n → [{9,10}, {14..17}]. */
export const runsOf = (mask: bigint): Run[] => {
  const slots = slotsOf(mask);
  const out: Run[] = [];
  for (let i = 0; i < slots.length;) {
    let j = i;
    while (j + 1 < slots.length && slots[j + 1] === slots[j]! + 1) j += 1;
    const from = slots[i]!;
    const to = slots[j]!;
    out.push({ from, to, length: to - from + 1, mask: (full(to - from + 1)) << BigInt(from) });
    i = j + 1;
  }
  return out;
};

/** The run of `mask` that contains `slot`, or 0n when that bit is dark: a grab takes the whole run. */
export const runAt = (mask: bigint, slot: number): bigint => runsOf(mask).find((r) => slot >= r.from && slot <= r.to)?.mask ?? 0n;

/**
 * The starts from which `length` slots in a row are all lit: the same fold the
 * engine runs over a hand's free slots before shifting it by a leg's offset.
 */
export const fitting = (mask: bigint, length: number): bigint => {
  let acc = mask;
  for (let k = 1; k < length; k += 1) acc &= mask >> BigInt(k);
  return length <= 0 ? 0n : acc;
};

/**
 * The first free run with the same shape as `wanted` (same length, one piece)
 * at or after `wanted`'s first slot, else the first anywhere: what "Next free
 * run" offers after a refusal. Null when nothing that long is free.
 */
export const nextFreeRun = (offered: bigint, wanted: bigint, bits: number): bigint | null => {
  const slots = slotsOf(wanted);
  if (slots.length === 0) return null;
  const length = slots.length;
  const starts = fitting(offered & full(bits), length);
  if (starts === 0n) return null;
  const first = slots[0]!;
  const later = starts >> BigInt(first);
  const pick = later !== 0n ? first + slotsOf(later & -later)[0]! : slotsOf(starts & -starts)[0]!;
  return full(length) << BigInt(pick);
};

/** A mask for people: "9–11, 14"; "—" when empty. */
export const label = (mask: bigint): string => (mask === 0n ? '—' : runs(slotsOf(mask)));

/** Slots a request may send, either form; null when neither reads. */
export const readSlots = (value: unknown): bigint | null => {
  if (typeof value === 'string') return fromHex(value);
  if (Array.isArray(value) && value.every((s) => Number.isInteger(s) && s >= 0)) return slotsMask(value as number[]);
  return null;
};
